import { useCallback } from 'react'
import {
  addCartItem,
  clearCart as clearCartRequest,
  removeCartItem,
  resolveCart,
  updateCartItem,
} from '../cartApi'
import type { Cart } from '../types'
import { normalizeQuantity } from './cartMutationUtils'
import { useCartEnsureCart } from './useCartEnsureCart'
import { useCartMutationRunner } from './useCartMutationRunner'

export const useCartMutations = ({
  cartId,
  applyCart,
  setBusy,
  setError,
}: {
  cartId: string | null
  applyCart: (nextCart: Cart) => void
  setBusy: (value: boolean) => void
  setError: (value: string) => void
}) => {
  const ensureCart = useCartEnsureCart({ cartId, applyCart })
  const { runMutation, setMutationError, getLastError } = useCartMutationRunner({ setBusy, setError })

  const refreshCart = useCallback(async () => {
    await runMutation(async () => {
      const resolved = await ensureCart()
      applyCart(resolved)
    })
  }, [applyCart, ensureCart, runMutation])

  const addItem = useCallback(
    async (productId: string, quantity = 1) => {
      const cleanProductId = productId.trim()
      if (!cleanProductId) {
        setMutationError('Prodotto non valido')
        return false
      }

      return runMutation(async () => {
        const resolved = await ensureCart()
        const nextCart = await addCartItem(
          resolved.cartId,
          cleanProductId,
          normalizeQuantity(quantity),
        )
        applyCart(nextCart)
      })
    },
    [applyCart, ensureCart, runMutation, setMutationError],
  )

  const updateItemQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      const cleanItemId = itemId.trim()
      if (!cleanItemId) {
        setMutationError('Item carrello non valido')
        return false
      }

      return runMutation(async () => {
        const resolved = await ensureCart()
        const nextCart = await updateCartItem(
          resolved.cartId,
          cleanItemId,
          normalizeQuantity(quantity),
        )
        applyCart(nextCart)
      })
    },
    [applyCart, ensureCart, runMutation, setMutationError],
  )

  const removeItem = useCallback(
    async (itemId: string) => {
      const cleanItemId = itemId.trim()
      if (!cleanItemId) {
        setMutationError('Item carrello non valido')
        return false
      }

      return runMutation(async () => {
        const resolved = await ensureCart()
        const nextCart = await removeCartItem(resolved.cartId, cleanItemId)
        applyCart(nextCart)
      })
    },
    [applyCart, ensureCart, runMutation, setMutationError],
  )

  const clearCart = useCallback(async () => {
    return runMutation(async () => {
      const resolved = await ensureCart()
      await clearCartRequest(resolved.cartId)
      const recreated = await resolveCart({
        cartId: resolved.cartId,
      })
      applyCart(recreated)
    })
  }, [applyCart, ensureCart, runMutation])

  return {
    ensureCart,
    refreshCart,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCart,
    getLastError,
  }
}
